import { useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

/** Stagger after badge horizontal reveal starts (see HotspotOverlayBadge). */
const CARD_REVEAL_DELAY = 0.35;

/**
 * Card reveals via clip-path after layout is stable (fonts + images).
 * Avoids width/height tweens that inflate then collapse when text reflows.
 */
export function HotspotOverlayCard({ children }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);

  useGSAP(
    () => {
      const inner = innerRef.current;
      if (!inner) return undefined;

      let tween = null;
      let cancelled = false;

      gsap.set(inner, {
        clipPath: 'inset(0 100% 0 0)',
        WebkitClipPath: 'inset(0 100% 0 0)',
      });

      const runReveal = async () => {
        await document.fonts.ready;

        const imgs = inner.querySelectorAll('img');
        await Promise.all(
          [...imgs].map((img) =>
            img.complete
              ? Promise.resolve()
              : new Promise((resolve) => {
                  img.addEventListener('load', resolve, { once: true });
                  img.addEventListener('error', resolve, { once: true });
                }),
          ),
        );

        if (cancelled) return;

        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        if (cancelled) return;

        const radius =
          parseFloat(getComputedStyle(inner).borderTopLeftRadius) || 32;
        const hidden = `inset(0 100% 0 0 round ${radius}px)`;
        const shown = `inset(0 0% 0 0 round ${radius}px)`;

        gsap.set(inner, {
          clipPath: hidden,
          WebkitClipPath: hidden,
        });

        tween = gsap.to(inner, {
          clipPath: shown,
          WebkitClipPath: shown,
          duration: 0.65,
          delay: CARD_REVEAL_DELAY,
          ease: 'power3.out',
        });
      };

      runReveal();

      return () => {
        cancelled = true;
        tween?.kill();
      };
    },
    { scope: outerRef },
  );

  return (
    <div ref={outerRef} className="hotspot-overlay__card-wrap">
      <div className="hotspot-overlay__card-clip">
        <div ref={innerRef} className="hotspot-overlay__card">
          {children}
        </div>
      </div>
    </div>
  );
}
